import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { scrollToId } from "@/App";

const IMG = {
  womens: "https://images.unsplash.com/photo-1445205170230-053b83016050?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  mens: "https://images.unsplash.com/photo-1617137968427-85924c800a22?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  arabic: "https://images.unsplash.com/photo-1645865675130-12f815af759e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  sport: "https://images.unsplash.com/photo-1617238749996-ab4c0f9fba57?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  accessories: "https://images.unsplash.com/photo-1584917865442-de89df76afd3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  perfume: "https://images.unsplash.com/photo-1705936118918-870095881e61?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  footwear: "https://images.unsplash.com/photo-1635855374289-b6ca7fe114ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

const CATEGORIES = [
  { id: "womens", name: "Women's Fabric Wholesale", moq: "MOQ 100 m", origin: "Silks, chiffons, georgettes & cottons", tag: "fashion", span: "lg:col-span-2 lg:row-span-2", img: IMG.womens },
  { id: "mens", name: "Men's Wholesale", moq: "MOQ 150 pcs", origin: "Suits, linens & shirting", tag: "fashion", span: "", img: IMG.mens },
  { id: "arabic", name: "Arabic Wear Wholesale", moq: "MOQ 100 pcs", origin: "Abayas, thobes & jalabiyas", tag: "fashion", span: "", img: IMG.arabic },
  { id: "sport", name: "Sportswear Wholesale", moq: "MOQ 300 pcs", origin: "Performance knits & tracksuits", tag: "fashion", span: "", img: IMG.sport },
  { id: "accessories", name: "Accessories Wholesale", moq: "MOQ 500 pcs", origin: "Belts, caps & seasonal trims", tag: "lifestyle", span: "", img: IMG.accessories },
  { id: "perfume", name: "Perfume Wholesale", moq: "MOQ 240 units", origin: "Oud, musk & French blends", tag: "lifestyle", span: "lg:col-span-2", img: IMG.perfume },
  { id: "footwear", name: "Footwear Wholesale", moq: "MOQ 300 pairs", origin: "Leather formals & casuals", tag: "lifestyle", span: "", img: IMG.footwear },
];

const TABS = [
  { id: "all", label: "All Segments" },
  { id: "fashion", label: "Fashion" },
  { id: "lifestyle", label: "Lifestyle" },
];

export default function Categories() {
  const [tab, setTab] = useState("all");
  const visible = CATEGORIES.filter((c) => tab === "all" || c.tag === tab);

  return (
    <section id="categories" data-testid="wholesale-categories-section" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 bg-[#12141B]">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-8"
        >
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#D4AF37]/80 mb-4">Clothing Wholesale — 7 Core Segments</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug max-w-2xl">
              One trading house. <span className="italic text-gold-gradient">Every rack your store needs.</span>
            </h2>
          </div>
          <div className="flex gap-2" role="tablist" data-testid="category-filter-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                data-testid={`category-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-5 py-2.5 text-xs tracking-[0.18em] uppercase border transition-colors duration-300 ${
                  tab === t.id
                    ? "border-[#D4AF37] bg-[#D4AF37] text-[#0A0B0E]"
                    : "border-[#D4AF37]/25 text-slate-300 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 auto-rows-[240px] gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((c, i) => (
              <motion.button
                layout
                key={c.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => scrollToId("#contact")}
                data-testid={`category-card-${c.id}`}
                className={`card-shine group relative overflow-hidden border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 transition-colors duration-500 text-left ${c.span}`}
              >
                <img
                  src={c.img}
                  alt={c.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/35 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-6">
                  <span className="text-[10px] font-mono tracking-[0.3em] text-[#D4AF37]/80 uppercase">{String(i + 1).padStart(2, "0")} — {c.moq}</span>
                  <h3 className="font-display text-xl sm:text-2xl text-amber-100 mt-1">{c.name}</h3>
                  <p className="text-sm font-light text-slate-300 mt-1">{c.origin}</p>
                  <span className="mt-3 text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    Samples dispatched on request →
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
